import { useState } from 'react';

import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  DropdownOption,
  GroupMembership,
  PeopleOutline,
  SearchDropdownInput,
  useToast,
  WalletOutline,
} from '@aslanpay/portal-common-lib';

import useCampaignManagers from 'app/hooks/useCampaignManagers';
import useFetchIndividuals from 'app/hooks/useFetchIndividuals';
import { PrideRow } from 'app/utils/table/campaigns/prides';

import ApplyToPridesModal from './ApplyToPridesModal';

const SEARCH_PAGE_SIZE = 20;

interface ManagePrideManagersModalProps {
  isOpen: boolean;
  onClose: () => void;
  pride: PrideRow;
  /** Every pride of the campaign, for copying this one's people onto others. */
  prides: PrideRow[];
  companyId: string;
  memberships: GroupMembership[];
  nameOf: (id: string) => string;
}

/** Who decides one pride. An appointment belongs to a pride, so this is the whole of that decision. */
function ManagePrideManagersModal({
  isOpen,
  onClose,
  pride,
  prides,
  companyId,
  memberships,
  nameOf,
}: ManagePrideManagersModalProps) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [query, setQuery] = useState('');
  const [isApplying, setIsApplying] = useState(false);

  const { data: found, isInitialLoading: isSearching } = useFetchIndividuals({
    companyId: [companyId],
    role: ['EMPLOYEE'],
    query,
    page: 0,
    size: SEARCH_PAGE_SIZE,
    enabled: isOpen,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['fetchCampaigns'] });

  const onError = () =>
    toast({
      position: 'top-right',
      status: 'error',
      title: 'Could not change who decides this pride',
      description: 'Nothing was saved. Please try again.',
    });

  const { mutateAsync: appointAsync, isLoading: isAppointing } =
    useCampaignManagers.useAppointCampaignManagers({ onError });
  const { mutateAsync: removeAsync, isLoading: isRemoving } =
    useCampaignManagers.useRemoveCampaignManagers({ onError });

  const isSaving = isAppointing || isRemoving;

  const members = memberships.filter((member) => member.groupId === pride.groupId);
  const memberIds = new Set(members.map((member) => member.individualId));

  const fromMember = (member: GroupMembership, groupName: string) => ({
    value: member.individualId,
    text: member.individualName,
    jobTitle: member.jobTitle ?? undefined,
    isPotManager: member.membershipRole === 'POT_MANAGER',
    isPrideMember: true,
    groupName,
  });

  const potManagerIds = new Set(
    members.filter((member) => member.membershipRole === 'POT_MANAGER').map((m) => m.individualId)
  );

  /**
   * What a person is to this pride, shown wherever they are named.
   * <p>
   * Holding the pot and belonging to the pride are different things and a person can be either, both or
   * neither, so they are marked separately rather than collapsed into one label.
   */
  const marksFor = (id: string) => (
    <>
      {potManagerIds.has(id) && (
        <WalletOutline
          boxSize="14px"
          color="brand.darkPurple"
          aria-label="Holds this pride's pot"
          data-testid={`pot-manager-mark-${id}`}
        />
      )}
      {memberIds.has(id) && (
        <PeopleOutline
          boxSize="14px"
          color="brand.darkPurple"
          aria-label="Belongs to this pride"
          data-testid={`pride-member-mark-${id}`}
        />
      )}
    </>
  );

  /**
   * The pride's pot managers first — whoever answers for its points is the likeliest to answer for its
   * winner — then the rest of its own members, then anyone found by searching.
   */
  const options: DropdownOption[] = [
    ...members
      .filter((member) => member.membershipRole === 'POT_MANAGER')
      .map((member) => fromMember(member, 'Pot managers')),
    ...pride.managerIds
      .filter((id) => !memberIds.has(id) && !(found?.list ?? []).some((person) => person.id === id))
      .map((id) => ({
        value: id,
        text: nameOf(id),
        isPotManager: false,
        groupName: 'Already appointed',
      })),
    ...members
      .filter((member) => member.membershipRole !== 'POT_MANAGER')
      .map((member) => fromMember(member, 'In this pride')),
    ...(found?.list ?? [])
      .filter((person) => !memberIds.has(person.id))
      .map((person) => ({
        value: person.id,
        text: `${person.firstName} ${person.lastName}`,
        jobTitle: person.jobTitle,
        isPotManager: false,
        groupName: 'Elsewhere in the company',
      })),
  ];

  const renderPerson = (option: DropdownOption) => {
    const isAppointed = pride.managerIds.includes(option.value as string);

    return (
      <Flex align="center" gap="8px" w="full">
        <Text textStyle={isAppointed ? 'smallMedium' : 'smallRegular'} color="brand.black">
          {option.text}
        </Text>
        {option.jobTitle && (
          <Text textStyle="extraSmallRegular" color="brand.darkGray">
            {option.jobTitle}
          </Text>
        )}
        {marksFor(option.value as string)}
        {isAppointed && (
          <Flex ml="auto" data-testid={`appointed-${option.value}`}>
            <Badge value="Appointed" status="active" size="sm" />
          </Flex>
        )}
      </Flex>
    );
  };

  /** One pride, one call each way, so a failure is about the pride in front of the reader. */
  const onChangeManagers = async (selected: string[]) => {
    const added = selected.filter((id) => !pride.managerIds.includes(id));
    const removed = pride.managerIds.filter((id) => !selected.includes(id));

    try {
      if (added.length > 0) {
        await appointAsync({ campaignId: pride.id, individualIds: added });
      }
      if (removed.length > 0) {
        await removeAsync({ campaignId: pride.id, individualIds: removed });
      }
    } finally {
      refresh();
    }
  };

  const onApplyToPrides = async (prideIds: string[]) => {
    try {
      await Promise.all(
        prideIds.map((campaignId) => appointAsync({ campaignId, individualIds: pride.managerIds }))
      );

      toast({
        position: 'top-right',
        status: 'success',
        title: 'Campaign managers appointed',
        description: `They now decide ${prideIds.length} more ${
          prideIds.length === 1 ? 'pride' : 'prides'
        }.`,
      });

      setIsApplying(false);
    } finally {
      refresh();
    }
  };

  return (
    <>
      <Modal isOpen={isOpen && !isApplying} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent data-testid="manage-pride-managers-modal">
          <ModalHeader>Who decides {pride.prideName}?</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex flexDir="column" gap="12px">
              <Text textStyle="extraSmallRegular" color="brand.darkGray">
                Whoever is appointed here reads this pride&apos;s nominations and picks its winner.
                Until somebody is, no winner can be recorded.
              </Text>
              <Flex flexDir="column" gap="6px" data-testid="appointed-summary">
                <Text textStyle="extraSmallRegular" color="brand.darkGray">
                  {pride.managerIds.length === 0
                    ? 'Nobody is appointed to this pride yet.'
                    : `Appointed to this pride (${pride.managerIds.length})`}
                </Text>
                {pride.managerIds.length > 0 && (
                  <Flex wrap="wrap" gap="10px">
                    {pride.managerIds.map((id) => (
                      <Flex key={id} align="center" gap="4px">
                        <Badge value={nameOf(id)} status="active" size="sm" />
                        {marksFor(id)}
                      </Flex>
                    ))}
                  </Flex>
                )}
              </Flex>
              <SearchDropdownInput
                selected={pride.managerIds}
                options={options}
                isLoading={isSearching}
                isDisabled={isSaving}
                placeholder="Search or pick people…"
                itemName="person"
                onSearch={setQuery}
                onChange={onChangeManagers}
                renderText={renderPerson}
                data-testid="manager-picker"
              />
            </Flex>
          </ModalBody>
          <ModalFooter gap="12px">
            <Button
              variant="outline"
              mr="auto"
              isDisabled={pride.managerIds.length === 0 || isSaving}
              onClick={() => setIsApplying(true)}
              data-testid="apply-elsewhere"
            >
              Apply to other prides
            </Button>
            <Button onClick={onClose} data-testid="done-managing-managers">
              Done
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ApplyToPridesModal
        isOpen={isApplying}
        onClose={() => setIsApplying(false)}
        source={pride}
        targets={prides.filter((other) => other.id !== pride.id)}
        names={pride.managerIds.map(nameOf)}
        isSaving={isSaving}
        onApply={onApplyToPrides}
      />
    </>
  );
}

export default ManagePrideManagersModal;
